import React, { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [academicYear, setAcademicYear] = useState(() => {
    // Initialize academic year from localStorage or default to current year
    const storedAcademicYear = localStorage.getItem("academicYear");
    if (storedAcademicYear !== null) return storedAcademicYear;

    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    return `${currentYear}-${nextYear.toString().slice(-2)}`;
  });
  const [loading, setLoading] = useState(true);
  const logoutTimer = useRef(null);
  const navigate = useNavigate();

  const clearLogoutTimer = () => {
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
  };

  const scheduleAutoLogout = (expirationMs) => {
    clearLogoutTimer();
    if (!expirationMs || expirationMs <= 0) return;
    logoutTimer.current = setTimeout(() => {
      handleLogout(true);
    }, expirationMs);
  };

  const handleLogout = (expired = false) => {
    setUserRole(null);
    setBranches([]);
    setSelectedBranchId(null);
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("branches");
    localStorage.removeItem("selectedBranchId");
    localStorage.removeItem("academicYear"); // Clear academic year from localStorage
    clearLogoutTimer();
    if (expired) {
      window.alert('Your session has expired. Please log in again.');
    }
    navigate('/login');
  };

  const updateAcademicYear = (year) => {
    setAcademicYear(year);
    localStorage.setItem("academicYear", year);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const storedBranches = JSON.parse(localStorage.getItem("branches") || "[]");
    const storedSelectedBranchId = localStorage.getItem("selectedBranchId");
    const storedAcademicYear = localStorage.getItem("academicYear");

    if (token) {
      const decoded = parseJwt(token);
      if (decoded && decoded.exp) {
        const expiresAt = decoded.exp * 1000;
        if (Date.now() >= expiresAt) {
          handleLogout(true);
          return;
        }
        scheduleAutoLogout(expiresAt - Date.now());
      }
    }

    setUserRole(role);
    setBranches(storedBranches);
    if (storedSelectedBranchId) {
      setSelectedBranchId(storedSelectedBranchId);
    } else if (storedBranches.length > 0) {
      setSelectedBranchId(storedBranches[0]._id);
      localStorage.setItem("selectedBranchId", storedBranches[0]._id);
    }
    if (storedAcademicYear) {
      setAcademicYear(storedAcademicYear);
    }

    setLoading(false);
  }, []);

  const updateSelectedBranch = (branchId) => {
    setSelectedBranchId(branchId);
    localStorage.setItem("selectedBranchId", branchId);
  };

  const updateAuthData = (data) => {
    setUserRole(data.role);
    setBranches(data.branches || []);

    if (data.branches && data.branches.length > 0) {
      const initialBranchId = data.branches[0]._id;
      setSelectedBranchId(initialBranchId);
      localStorage.setItem("selectedBranchId", initialBranchId);
      localStorage.setItem("branches", JSON.stringify(data.branches));
    }

    localStorage.setItem("role", data.role);
    localStorage.setItem("token", data.token);

    const decoded = parseJwt(data.token);
    if (decoded && decoded.exp) {
      const expiresAt = decoded.exp * 1000;
      scheduleAutoLogout(expiresAt - Date.now());
    }
  };

  return (
    <AuthContext.Provider value={{ 
      userRole, 
      branches, 
      selectedBranchId, 
      setSelectedBranchId: updateSelectedBranch,
      academicYear,
      setAcademicYear: updateAcademicYear,
      updateAuthData,
      logout: handleLogout
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};